import { useMemo } from 'react';
import { StatsMetricProps } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { featureFlags } from '../../../../feature-flags';
import { RoiSummary } from '../../components/roi-summary';
import { MessagingOverview } from '../../types';

interface MCTSummaryProps {
  data: MessagingOverview;
  isLoading?: boolean;
}

export const MCTSummary = ({ data, isLoading }: MCTSummaryProps) => {
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.missedCallTextRoiDemoData);

  const summaryCardsData = useMemo<StatsMetricProps[]>(() => {
    return [
      {
        uniqueId: 'messages-sent',
        title: t('Messages Sent'),
        data: [
          {
            label: t('{{count}} Patients', { count: data.leadsContacted.totalPatients }),
            value: formatters.value(data.leadsContacted.messagesSent),
            infoHoverText: t('A total of {{messages}} messages were sent to {{patients}} patients', {
              messages: data.leadsContacted.messagesSent,
              patients: data.leadsContacted.totalPatients,
            }),
          },
        ],
      },
      {
        uniqueId: 'appointments-scheduled',
        title: t('Appointments Scheduled'),
        data: [
          {
            label: t('{{count}} Patients', { count: data.patientsScheduled.totalPatients }),
            value: formatters.value(data.patientsScheduled.appointmentsScheduledCount),
            infoHoverText: t('A total of {{appointments}} appointments were scheduled from {{patients}} patients', {
              appointments: data.patientsScheduled.appointmentsScheduledCount,
              patients: data.patientsScheduled.totalPatients,
            }),
          },
        ],
      },
    ];
  }, [data, t]);

  return (
    <RoiSummary
      isDemoAccount={isDemoAccount}
      isLoading={isLoading}
      productionAppointmentsCount={data?.revenueGenerated?.totalCompletedAppts}
      productionPatientsCount={
        (data?.revenueGenerated?.newPatientsCompletedAppts || 0) +
        (data?.revenueGenerated?.existingPatientsCompletedAppts || 0)
      }
      summaryCardsData={summaryCardsData}
    />
  );
};
